import type { FilterValueStatus } from "@octopusdeploy/octopus-server-client";
import { FilterValueStatusValues } from "@octopusdeploy/octopus-server-client";
import { createQueryParam, numberQueryParam, optionalStringQueryParam, useQueryStringParams } from "@octopusdeploy/portal-routes";
import { useMemo } from "react";
import type { ProjectTenantVariablesFilterState } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
export function useScopedProjectTemplatesFilterState(): [
    ProjectTenantVariablesFilterState,
    (filter: ProjectTenantVariablesFilterState) => void
] {
    const searchParameter = optionalStringQueryParam("search");
    const environmentParameter = optionalStringQueryParam("environment");
    const excludedEnvironmentParameter = optionalStringQueryParam("excludedEnvironment");
    const tenantParameter = optionalStringQueryParam("tenant");
    const excludedTenantParameter = optionalStringQueryParam("excludedTenant");
    const tagsParameter = optionalStringQueryParam("tags");
    const excludedTagsParameter = optionalStringQueryParam("excludedTags");
    const valueStatusParameter = createQueryParam("valueStatus", validateValueStatus, (v) => v);
    const pageNumberParameter = numberQueryParam("pageNumber");
    const projectIdParameter = optionalStringQueryParam("projectId");
    const excludedProjectIdParameter = optionalStringQueryParam("excludedProjectId");
    const [filter, setFilter] = useQueryStringParams([
        searchParameter,
        environmentParameter,
        excludedEnvironmentParameter,
        tenantParameter,
        excludedTenantParameter,
        tagsParameter,
        excludedTagsParameter,
        valueStatusParameter,
        pageNumberParameter,
        projectIdParameter,
        excludedProjectIdParameter,
    ]);
    function setProjectTenantVariablesFilterState(filter: ProjectTenantVariablesFilterState) {
        setFilter({
            search: filter.filterByName,
            environment: filter.filterByEnvironment,
            excludedEnvironment: filter.filterByExcludedEnvironment,
            tenant: filter.filterByTenant,
            excludedTenant: filter.filterByExcludedTenant,
            tags: filter.filterByTags.length > 0 ? filter.filterByTags.join(",") : undefined,
            excludedTags: filter.filterByExcludedTags.length > 0 ? filter.filterByExcludedTags.join(",") : undefined,
            valueStatus: filter.filterByValueStatus,
            pageNumber: filter.pageNumber,
            projectId: filter.filterByProjectId,
            excludedProjectId: filter.filterByExcludedProjectId,
        });
    }
    const projectTenantVariablesFilterState = useMemo((): ProjectTenantVariablesFilterState => ({
        filterByName: filter.search,
        filterByEnvironment: filter.environment,
        filterByExcludedEnvironment: filter.excludedEnvironment,
        filterByTenant: filter.tenant,
        filterByExcludedTenant: filter.excludedTenant,
        filterByTags: filter.tags ? filter.tags.split(",") : [],
        filterByExcludedTags: filter.excludedTags ? filter.excludedTags.split(",") : [],
        filterByValueStatus: filter.valueStatus,
        filterByTemplateId: undefined,
        filterByProjectId: filter.projectId,
        filterByExcludedProjectId: filter.excludedProjectId,
        filterByLibraryVariableSetId: undefined,
        filterByExcludedLibraryVariableSetId: undefined,
        pageNumber: filter.pageNumber ?? 1,
    }), [filter.search, filter.environment, filter.excludedEnvironment, filter.tenant, filter.excludedTenant, filter.tags, filter.excludedTags, filter.valueStatus, filter.projectId, filter.excludedProjectId, filter.pageNumber]);
    return [projectTenantVariablesFilterState, setProjectTenantVariablesFilterState];
}
function validateValueStatus(valueStatus: string | undefined): FilterValueStatus | undefined {
    if (valueStatus === undefined)
        return undefined;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const possibleStatus = valueStatus as FilterValueStatus;
    return Object.values(FilterValueStatusValues).includes(possibleStatus) ? possibleStatus : undefined;
}
